<script setup lang="ts">
/**
 * A Hub tile for an assistant the caller can see but not use.
 *
 * Same footprint as AssistantCard so the grid stays even, but it is a
 * button, not a link: there is nowhere to go. Clicking it opens the
 * access-request dialog. It reads as unavailable at a glance — desaturated,
 * a lock, and a short "No access" (or "Requested <date>" once asked) — so
 * nobody mistakes it for an assistant they can open.
 *
 * Only name, description and icon are shown; the catalog sends nothing else
 * for a locked assistant.
 */
import { Lock } from "lucide-vue-next";
import { computed } from "vue";
import { shortDate, type CatalogAssistant } from "../api/accessRequests";
import {
  DEFAULT_ASSISTANT_ICONS,
  resolveAssistantIcon,
} from "../assistant-icons";

const props = withDefaults(
  defineProps<{ assistant: CatalogAssistant; index?: number }>(),
  { index: 0 },
);
const emit = defineEmits<{ (e: "open", a: CatalogAssistant): void }>();

const iconComp = computed(() =>
  resolveAssistantIcon(
    props.assistant.icon ?? DEFAULT_ASSISTANT_ICONS[props.assistant.slug],
  ),
);

const monogram = computed(() => {
  const words = props.assistant.name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
});

const tagline = computed(() => {
  const text = props.assistant.description.replace(/\s+/g, " ").trim();
  const cut = text.search(/[.!?](\s|$)/);
  if (cut >= 40 && cut <= 160) return text.slice(0, cut + 1);
  return text;
});

const requested = computed(() =>
  props.assistant.request_status === "open"
    ? `Requested ${shortDate(props.assistant.request?.created_at)}`.trim()
    : "",
);
</script>

<template>
  <button
    type="button"
    class="lcard rise-in"
    :style="{ animationDelay: `${Math.min(index, 12) * 45}ms` }"
    :aria-label="`${assistant.name} — no access. ${requested || 'Request access'}`"
    @click="emit('open', assistant)"
  >
    <span class="lcard__badge" aria-hidden="true">
      <component
        :is="iconComp"
        v-if="iconComp"
        :size="22"
        :stroke-width="1.9"
      />
      <template v-else>{{ monogram }}</template>
    </span>
    <span class="lcard__head">
      <span class="lcard__name">{{ assistant.name }}</span>
      <span v-if="assistant.maturity === 'pilot'" class="lcard__pilot">
        Pilot
      </span>
    </span>
    <span v-if="tagline" class="lcard__tagline">{{ tagline }}</span>
    <span :class="['lcard__state', requested && 'lcard__state--requested']">
      <Lock :size="13" :stroke-width="2.2" aria-hidden="true" />
      {{ requested || "No access" }}
    </span>
  </button>
</template>

<style scoped>
.lcard {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.45rem;
  min-height: 9.5rem;
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  border: 1px dashed color-mix(in srgb, var(--color-muted) 35%, transparent);
  background: color-mix(in srgb, var(--color-surface) 35%, transparent);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  filter: grayscale(1);
  opacity: 0.72;
  transition:
    opacity var(--dur-2) var(--ease-out),
    border-color var(--dur-2) var(--ease-out);
}
@media (hover: hover) {
  .lcard:hover {
    opacity: 0.95;
    border-color: color-mix(in srgb, var(--color-muted) 60%, transparent);
  }
}
.lcard:focus-visible {
  outline: none;
  box-shadow: var(--ring);
  opacity: 0.95;
}
.lcard__badge {
  display: grid;
  place-items: center;
  flex: none;
  width: 2.6rem;
  height: 2.6rem;
  margin-bottom: 0.2rem;
  border-radius: 30%;
  background: color-mix(in srgb, var(--color-muted) 14%, transparent);
  color: var(--color-muted);
  font-size: 0.92rem;
  font-weight: 700;
}
.lcard__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}
.lcard__name {
  font-size: 1.02rem;
  font-weight: 650;
  line-height: 1.3;
  color: var(--color-ink);
}
.lcard__pilot {
  padding: 0.05rem 0.45rem;
  border-radius: var(--radius-pill);
  border: 1px solid color-mix(in srgb, var(--color-muted) 45%, transparent);
  color: var(--color-muted);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.lcard__tagline {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  font-size: 0.84rem;
  line-height: 1.5;
  color: var(--color-muted);
}
.lcard__state {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: auto;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--color-muted);
}
.lcard__state--requested {
  color: var(--color-ink);
}
@media (max-width: 560px) {
  .lcard {
    min-height: 0;
  }
}
</style>
